import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Download, Loader2, AlertTriangle, FileBarChart2 } from "lucide-react";
import { toast } from "sonner";
import { reportsApi, TenantReport } from "@/lib/reports-api";
import { sitesApi, SiteSummary } from "@/lib/sites-api";
import { departmentsApi, Department, departmentLabel } from "@/lib/departments-api";
import { departmentOptions, pickedIds, pickedLabel, siteOptions } from "@/lib/picker-options";
import { SearchMultiSelect } from "@/components/SearchMultiSelect";
import {
  DateRange, rangeToQuery,
} from "@/lib/date-range";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { useAnchoredDateRange } from "@/hooks/useAnchoredDateRange";
import type { ExportTable } from "@/lib/export";
import { ExportMenu } from "@/components/ExportMenu";
import { ApiError } from "@/lib/api";

const CHART = {
  present: "hsl(217 91% 50%)",
  partial: "hsl(43 96% 56%)",
  exception: "hsl(0 72% 51%)",
  missed: "hsl(38 92% 50%)",
  late: "hsl(280 65% 55%)",
};
const STATUS_COLORS: Record<string, string> = {
  PRESENT: CHART.present,
  PARTIAL: CHART.partial,
  EXCEPTION: CHART.exception,
  MISSED: CHART.missed,
};
const TOOLTIP_STYLE = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

/** Minutes → "4h 12m" / "45m" / "—". */
function formatDuration(min: number | null): string {
  if (min == null) return "—";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const Reports = () => {
  const [data, setData] = useState<TenantReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("summary");

  // Reports is analytical — default to a period, not "today" like the dashboard.
  const { range, setRange, preset, selectPreset } = useAnchoredDateRange("last30");
  // Several of each may be picked (FEAT-2026-030); empty means all.
  const [siteFilter, setSiteFilter] = useState<string[]>([]);
  const [deptFilter, setDeptFilter] = useState<string[]>([]);
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const siteChoices = useMemo(() => siteOptions(sites), [sites]);
  const deptChoices = useMemo(() => departmentOptions(departments, { includesSubunits: true }), [departments]);

  useEffect(() => {
    sitesApi.list({ pageSize: 200 }).then((r) => setSites(r.data)).catch(() => { /* non-fatal */ });
    departmentsApi.listAll().then(setDepartments).catch(() => { /* non-fatal */ });
  }, []);

  // Depend on primitives so an identical range object doesn't refetch.
  const { from: rangeFrom, to: rangeTo } = range;

  useEffect(() => {
    if (rangeFrom > rangeTo) return; // reversed custom range — let the user fix it
    let cancelled = false;
    setLoading(true);
    reportsApi
      .tenantReport({
        ...rangeToQuery({ from: rangeFrom, to: rangeTo }),
        siteIds: pickedIds(siteFilter),
        departmentIds: pickedIds(deptFilter),
      })
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load report";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rangeFrom, rangeTo, siteFilter, deptFilter]);


  // Late/missed by department for the Exceptions tab (derived, not a 2nd fetch).
  const exceptionsByDept = useMemo(() => {
    if (!data) return [];
    return data.byDepartment
      .map((d) => ({ department: d.department, count: d.exception + d.missed }))
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [data]);

  /** The table for whichever tab is showing — exported as CSV, Excel or PDF. */
  const buildExportTable = (): ExportTable | null => {
    if (!data) return null;
    const stamp = `${data.meta.from}_${data.meta.to}`;
    const meta: Record<string, string> = {
      Period: `${data.meta.from} to ${data.meta.to}`,
      Site: pickedLabel(siteFilter, siteChoices, "All sites"),
      // Full paths in the export, so a reader can tell same-named units apart.
      Department: pickedLabel(
        deptFilter,
        departments.map((d) => ({ value: d.id, label: departmentLabel(d) })),
        "All departments",
      ),
    };
    if (tab === "summary") {
      const t = data.summary;
      return {
        filename: `report-summary_${stamp}`, title: "Attendance summary", meta,
        headers: ["Metric", "Value"],
        rows: [["Present", t.present], ["Partial", t.partial], ["Exception", t.exception],
               ["Missed", t.missed], ["Total records", t.total]],
      };
    }
    if (tab === "dept") {
      return {
        filename: `report-department_${stamp}`, title: "Attendance by department", meta,
        headers: ["Department", "Present", "Partial", "Exception", "Missed", "Total"],
        rows: data.byDepartment.map((d) => [d.department, d.present, d.partial, d.exception, d.missed, d.total]),
      };
    }
    if (tab === "site") {
      return {
        filename: `report-site_${stamp}`, title: "Attendance by site", meta,
        headers: ["Site", "Present", "Exception"],
        rows: data.bySite.map((x) => [x.site, x.present, x.exception]),
      };
    }
    if (tab === "exceptions") {
      return {
        filename: `report-exceptions_${stamp}`, title: "Exceptions by department", meta,
        headers: ["Department", "Late / missed"],
        rows: exceptionsByDept.map((d) => [d.department, d.count]),
      };
    }
    const a = data.approvals;
    return {
      filename: `report-approvals_${stamp}`, title: "Approval performance", meta,
      headers: ["Metric", "Value"],
      rows: [["Requests decided", a.decidedCount],
             ["Avg turnaround (min)", a.avgTurnaroundMinutes ?? ""],
             ["Within SLA %", a.withinSlaPct ?? ""],
             [`Overdue (open > ${a.slaHours}h)`, a.overdueCount],
             ["SLA hours", a.slaHours]],
    };
  };

  if (loading && !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <Card className="flex flex-col items-center justify-center gap-3 p-12 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <div>
          <h3 className="font-display font-semibold">Couldn't load the report</h3>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
        </div>
        <Button size="sm" onClick={() => window.location.reload()}>Try again</Button>
      </Card>
    );
  }

  if (!data) return null;

  const { summary, dailyTrend, statusBreakdown, byDepartment, bySite, approvals, lateness, latenessTrend } = data;
  const attendanceRate = summary.total ? Math.round((summary.present / summary.total) * 100) : 0;
  const exceptionRate = summary.total ? Math.round(((summary.exception + summary.missed) / summary.total) * 1000) / 10 : 0;

  return (
    <div className="space-y-5">
      {/* Filters (FEAT-2026-009): the range + site + department narrow every
          section. Department is Reports' distinguishing dimension. */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeFilter value={range} preset={preset} onSelectPreset={selectPreset} onChange={setRange} inputWidth="w-36" />

          <div className="w-44">
            <Label className="text-xs text-muted-foreground">Site</Label>
            <SearchMultiSelect
              label="Site"
              values={siteFilter}
              onChange={setSiteFilter}
              options={siteChoices}
              allLabel="All sites"
              noun={{ one: "site", many: "sites" }}
              className="mt-1 h-9"
            />
          </div>
          <div className="w-60">
            <Label className="text-xs text-muted-foreground">Department</Label>
            <SearchMultiSelect
              label="Department"
              values={deptFilter}
              onChange={setDeptFilter}
              options={deptChoices}
              allLabel="All departments"
              noun={{ one: "department", many: "departments" }}
              className="mt-1 h-9"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <ExportMenu build={buildExportTable} disabled={!data} />
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-card border">
          <TabsTrigger value="summary">Daily Summary</TabsTrigger>
          <TabsTrigger value="dept">Department</TabsTrigger>
          <TabsTrigger value="site">Site</TabsTrigger>
          <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
          <TabsTrigger value="approvals">Approvals</TabsTrigger>
        </TabsList>

        {/* Daily summary */}
        <TabsContent value="summary" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ReportStat label="Attendance rate" value={`${attendanceRate}%`} sub={`${summary.present} present`} />
            <ReportStat label="Partial" value={String(summary.partial)} sub="incomplete days" />
            <ReportStat label="Exceptions" value={String(summary.exception)} sub={`${exceptionRate}% of records`} tone="destructive" />
            <ReportStat label="Missed" value={String(summary.missed)} sub="no check-in" tone="destructive" />
          </div>
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-1">Attendance trend over time</h3>
            <p className="text-xs text-muted-foreground mb-4">Daily Present / Exception / Missed</p>
            <div className="h-72">
              {dailyTrend.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyTrend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="present" stroke={CHART.present} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="exception" stroke={CHART.exception} strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="missed" stroke={CHART.missed} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </div>
          </Card>

          <Card className="p-5 mt-4">
            <h3 className="font-display font-semibold mb-1">Arrival punctuality</h3>
            <p className="text-xs text-muted-foreground mb-4">
              {lateness.latePct == null
                ? "No arrivals could be measured in this period. Set a late threshold in Settings, or on a specific work location, to start tracking this."
                : `${lateness.lateDays} of ${lateness.measuredDays} measured arrivals were late (${lateness.latePct}%), averaging ${lateness.avgLateMinutes} minutes.`}
            </p>
            <div className="h-72">
              {lateness.measuredDays > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={latenessTrend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="late" name="Late arrivals" fill={CHART.late} />
                    <Bar dataKey="avgLateMinutes" name="Avg minutes late" fill={CHART.partial} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </div>
          </Card>
        </TabsContent>

        {/* Department */}
        <TabsContent value="dept">
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-4">Attendance by department</h3>
            <div className="h-80">
              {byDepartment.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byDepartment} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="department" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="present" stackId="a" fill={CHART.present} />
                    <Bar dataKey="partial" stackId="a" fill={CHART.partial} />
                    <Bar dataKey="exception" stackId="a" fill={CHART.exception} />
                    <Bar dataKey="missed" stackId="a" fill={CHART.missed} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </div>
          </Card>
        </TabsContent>

        {/* Site */}
        <TabsContent value="site">
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-4">Attendance by site</h3>
            <div className="h-80">
              {bySite.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bySite} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="site" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="present" fill={CHART.present} radius={[6, 6, 0, 0]} />
                    <Bar dataKey="exception" fill={CHART.exception} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty />}
            </div>
          </Card>
        </TabsContent>

        {/* Exceptions */}
        <TabsContent value="exceptions">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-4">Status breakdown</h3>
              <div className="h-64">
                {statusBreakdown.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusBreakdown} dataKey="value" nameKey="label" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {statusBreakdown.map((s) => <Cell key={s.status} fill={STATUS_COLORS[s.status] ?? "hsl(var(--muted-foreground))"} />)}
                      </Pie>
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <Empty />}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-4">Late / missed by department</h3>
              {exceptionsByDept.length ? (
                <ul className="divide-y">
                  {exceptionsByDept.map((r) => (
                    <li key={r.department} className="flex items-center justify-between py-2.5 text-sm">
                      <span>{r.department}</span>
                      <span className="font-semibold tabular-nums">{r.count}</span>
                    </li>
                  ))}
                </ul>
              ) : <Empty label="No exceptions in this period" />}
            </Card>
          </div>
        </TabsContent>

        {/* Approvals */}
        <TabsContent value="approvals">
          <Card className="p-5">
            <h3 className="font-display font-semibold mb-1">Request approval performance</h3>
            <p className="text-xs text-muted-foreground mb-4">
              Turnaround of requests decided in this period · SLA target {approvals.slaHours}h
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <ReportStat label="Avg turnaround" value={formatDuration(approvals.avgTurnaroundMinutes)} sub={`${approvals.decidedCount} decided`} tone="success" />
              <ReportStat label="Within SLA" value={approvals.withinSlaPct == null ? "—" : `${approvals.withinSlaPct}%`} sub={`≤ ${approvals.slaHours}h`} />
              <ReportStat label="Overdue" value={String(approvals.overdueCount)} sub={`open > ${approvals.slaHours}h`} tone={approvals.overdueCount > 0 ? "destructive" : undefined} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Empty = ({ label = "No data for this period" }: { label?: string }) => (
  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{label}</div>
);

const ReportStat = ({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "success" | "destructive" }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <FileBarChart2 className="h-4 w-4 text-muted-foreground" />
    </div>
    <div className={`mt-1 text-2xl font-semibold tracking-tight tabular-nums ${tone === "success" ? "text-success" : tone === "destructive" ? "text-destructive" : ""}`}>{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </Card>
);

export default Reports;
